/**
 * Informes imprimibles a partir de un listado filtrado.
 *
 * El «PDF» sale por el diálogo de impresión del navegador (Guardar como PDF) y
 * el Excel por `exportRowsToExcel`. No se añaden dependencias: el HTML que se
 * abre lleva su propio CSS y su propia cabecera.
 */

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

const fmtNow = () => new Date().toLocaleString('es-ES', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
});

/**
 * Construye el HTML completo del informe.
 *
 * @param {object} opts
 * @param {string} opts.title        Título grande del informe
 * @param {string} [opts.subtitle]   Línea de contexto (empresa, ámbito…)
 * @param {Array<{label:string,value:string}>} [opts.filters]  Filtros aplicados
 * @param {string[]} opts.columns    Cabeceras en orden
 * @param {Array<object>} opts.rows  Filas con las cabeceras como claves
 * @param {string} [opts.brand]      Pie de página
 */
export function buildReportHtml({ title, subtitle = '', filters = [], columns = [], rows = [], brand = 'L6 Ecosystem · CMDB' }) {
  const filtros = filters.filter((f) => f && String(f.value || '').trim());
  const thead = columns.map((c) => `<th>${escapeHtml(c)}</th>`).join('');
  const tbody = rows.map((row) => {
    const cells = columns.map((c) => `<td>${escapeHtml(row[c] ?? '—')}</td>`).join('');
    return `<tr>${cells}</tr>`;
  }).join('');

  return `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(title)}</title>
<style>
  @page { size: A4 landscape; margin: 12mm; }
  * { box-sizing: border-box; }
  body { font-family: "Segoe UI", Roboto, Arial, sans-serif; color: #111827; margin: 0; padding: 18px 24px; background: #fff; }
  .rp-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; border-bottom: 3px solid #4f46e5; padding-bottom: 14px; }
  .rp-brand { font-size: 11px; letter-spacing: .12em; text-transform: uppercase; color: #6b7280; }
  h1 { font-size: 22px; margin: 4px 0 2px; }
  .rp-sub { font-size: 12px; color: #4b5563; }
  .rp-stamp { text-align: right; font-size: 11px; color: #6b7280; white-space: nowrap; }
  .rp-total { display: inline-block; margin-top: 6px; padding: 3px 10px; border-radius: 999px; background: #eef2ff; color: #3730a3; font-weight: 600; font-size: 12px; }
  .rp-filters { display: flex; flex-wrap: wrap; gap: 6px; margin: 12px 0 4px; }
  .rp-chip { font-size: 11px; border: 1px solid #d1d5db; border-radius: 999px; padding: 2px 9px; color: #374151; background: #f9fafb; }
  .rp-chip b { color: #111827; }
  table { width: 100%; border-collapse: collapse; margin-top: 12px; font-size: 11px; }
  thead th { background: #f3f4f6; text-align: left; padding: 7px 8px; border-bottom: 2px solid #d1d5db; font-size: 10px; text-transform: uppercase; letter-spacing: .04em; color: #374151; }
  tbody td { padding: 6px 8px; border-bottom: 1px solid #e5e7eb; vertical-align: top; }
  tbody tr:nth-child(even) { background: #fafafa; }
  .rp-foot { margin-top: 18px; padding-top: 10px; border-top: 1px solid #e5e7eb; display: flex; justify-content: space-between; font-size: 10px; color: #6b7280; }
  .rp-toolbar { position: fixed; right: 18px; bottom: 18px; display: flex; gap: 8px; }
  .rp-toolbar button { border: 0; border-radius: 8px; padding: 10px 16px; font-size: 13px; font-weight: 600; cursor: pointer; background: #4f46e5; color: #fff; box-shadow: 0 6px 18px rgba(79,70,229,.35); }
  .rp-toolbar button.secondary { background: #111827; }
  @media print { .rp-toolbar { display: none; } body { padding: 0; } tbody tr:hover { background: transparent; } }
</style>
</head>
<body>
  <div class="rp-head">
    <div>
      <div class="rp-brand">${escapeHtml(brand)}</div>
      <h1>${escapeHtml(title)}</h1>
      ${subtitle ? `<div class="rp-sub">${escapeHtml(subtitle)}</div>` : ''}
      <div class="rp-total">${rows.length} registro${rows.length === 1 ? '' : 's'}</div>
    </div>
    <div class="rp-stamp">Generado el<br/><b>${escapeHtml(fmtNow())}</b></div>
  </div>
  ${filtros.length ? `<div class="rp-filters">${filtros.map((f) => `<span class="rp-chip">${escapeHtml(f.label)}: <b>${escapeHtml(f.value)}</b></span>`).join('')}</div>` : ''}
  <table>
    <thead><tr>${thead}</tr></thead>
    <tbody>${tbody || `<tr><td colspan="${Math.max(1, columns.length)}">Sin datos con los filtros aplicados.</td></tr>`}</tbody>
  </table>
  <div class="rp-foot">
    <span>${escapeHtml(brand)}</span>
    <span>Documento generado automáticamente. Los datos reflejan el inventario en el momento de la consulta.</span>
  </div>
  <div class="rp-toolbar">
    <button onclick="window.print()">Guardar PDF / Imprimir</button>
    <button class="secondary" onclick="window.close()">Cerrar</button>
  </div>
</body>
</html>`;
}

/** Abre el informe en una pestaña nueva y ofrece imprimir directamente. */
export function openReport({ autoPrint = false, ...opts }) {
  const html = buildReportHtml(opts);
  const win = window.open('', '_blank');
  if (!win) return false;
  win.document.open();
  win.document.write(html);
  win.document.close();
  if (autoPrint) {
    win.addEventListener('load', () => win.print(), { once: true });
  }
  return true;
}
